<template>
  <div v-if="!loading">
    <v-row no-gutters class="full-width full-height overflow-y-auto pa-4">
      <v-col cols="3" class="d-flex align-center">
        <v-select
          v-model="selectedYear"
          :items="yearOptions"
          label="Année de licence"
          color="third"
          item-color="third"
          hide-details
          clearable
          :menu-props="{ offsetY: true, maxHeight: 160 }"
          @change="selectedItems = []"
        />
      </v-col>
      <v-col cols="3" />
      <v-col cols="6" class="d-flex justify-end">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          placeholder="email@example.com"
          single-line
          clearable
          hide-details
          class="pb-4 pt-0 mt-0 col col-6"
          color="third"
          @click.stop
        />
      </v-col>
      <v-col cols="12">
        <v-data-table
          v-model="selectedItems"
          :search="search"
          :items="filteredLicenses"
          :items-per-page="5"
          item-key="key"
          :headers="headers"
          show-select
          :custom-filter="filterDataTableSession"
          checkbox-color="third"
          :height="tableHeight"
        >
          <template #item.horseType="{ item }">
            <v-select
              v-model="item.horseType"
              :items="horseTypes"
              :label="item.horseType || 'A renseigner'"
              width="150"
              single-line
              @change="manageRowSelection(item)"
            />
          </template>
          <template #item.actions="{ item }">
            <v-icon @click="openDeleteModal(item)">mdi-close-circle-outline</v-icon>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row no-gutters class="py-4">
      <div class="full-width d-flex justify-space-around align-center">
        <v-btn
          class="white--text pointer"
          height="52"
          color="third"
          :disabled="!licensesToUpdate.length"
          @click="openRefreshModal"
        >
          ACTUALISER
        </v-btn>
        <v-btn v-if="canUserPerformAction" :disabled="isButtonDisable" color="third" class="white--text pointer" height="52" @click="approveLicense">
          {{ confirmLabel }}
        </v-btn>
      </div>
    </v-row>
    <Modal v-if="showDeleteModal" :show="showDeleteModal" max-width="500" :close="closeDeleteModal" :show-dividers="false">
      <template #title>
        <div class="col-12">
          <h2 class="text-center third--text">Confirmation de suppression</h2>
        </div>
      </template>
      <template #text>
        <div class="d-flex flex justify-center">
          <span class="text-h6">
            Êtes-vous sûr de vouloir supprimer la licence de {{ selectedLicense.fullname }} ?
          </span>
        </div>
      </template>
      <template #actions>
        <div class="d-flex flex justify-center px-4">
          <v-btn class="ma-2 white--text" color="third" @click="closeDeleteModal"> Annuler </v-btn>
          <v-btn class="ma-2 white--text" color="third" @click="deleteLicense"> Confirmer </v-btn>
        </div>
      </template>
    </Modal>
    <Modal v-if="showRefreshModal" :show="showRefreshModal" max-width="500" :close="closeRefreshModal" :show-dividers="false">
      <template #title>
        <div class="col-12">
          <h2 class="text-center third--text">Confirmation des licences à mettre à jour</h2>
        </div>
      </template>
      <template #text>
        <v-data-table
          :headers="refreshHeaders"
          :items="selectedItems"
          :hide-default-footer="true"
          :items-per-page="-1"
        />
      </template>
      <template #actions>
        <div class="d-flex flex justify-center px-4">
          <v-btn class="ma-2 white--text" color="third" @click="closeRefreshModal"> Annuler </v-btn>
          <v-btn class="ma-2 white--text" color="third" @click="updateAllLicenses"> Confirmer </v-btn>
        </div>
      </template>
    </Modal>
  </div>
  <div v-else class="d-flex justify-center align-center full-height">
    <Loader />
  </div>
</template>

<script>
import { onBeforeMount, computed, ref } from '@vue/composition-api';

import { getCustomersLicensesToActivate, activateCustomersLicenses, deleteLicenseDynamo, refreshLicense } from '@/helpers/request';
import { sendNotification } from '@/helpers/utils';

import Loader from '@/components/common/Loader.vue';
import Modal from '@/components/common/Modal.vue';

import store from '@/store';

export default {
  components: { Loader, Modal },
  setup () {
    const allLicenses = ref([]);
    const selectedItems = ref([]);
    const selectedYear = ref(null);
    const search = ref(null);
    const loading = ref(false);
    const tableHeight = computed(() => window.innerHeight * 0.6);
    const selectedLicense = ref(null);
    const showDeleteModal = ref(false);
    const showRefreshModal = ref(false);

    const headers = ref([
      { text: 'Nom', value: 'lastname', width: '15%', align: 'center' },
      { text: 'Prénom', value: 'firstname', width: '15%', align: 'center' },
      { text: 'Email', value: 'email', width: '20%', align: 'center' },
      { text: 'Code de licence', value: 'licenseCode', width: '10%', align: 'center' },
      { text: 'Type', value: 'horseType', width: '15%', align: 'center' },
      { text: 'Date de fin de validité de la dernière licences', value: 'lastLicenseYear', width: '10%', align: 'center' },
      { text: 'Numéro de dernière licences', value: 'lastLicenseNumber', width: '10%', align: 'center' },
      { text: '', value: 'actions', width: '5%', align: 'center' }
    ]);

    const refreshHeaders = ref([
      { text: 'Nom', value: 'fullname', width: '40%', align: 'center' },
      { text: 'Année de licence', value: 'lastLicenseYear', width: '30%', align: 'center' },
      { text: 'Numéro de licence', value: 'lastLicenseNumber', width: '30%', align: 'center' }
    ]);

    const horseTypes = ref([
      { text: 'Poney (P)', value: 'P' },
      { text: 'Cheval (C)', value: 'C' },
      { text: 'Tourisme (T)', value: 'T' }
    ]);

    // Season "YYYY/YYYY+1" matches licenses where pegase_license_year is YYYY or YYYY+1
    const START_YEAR = 2023;

    const yearOptions = computed(() => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const endYear = now.getMonth() >= 8 ? currentYear + 1 : currentYear;
      const options = [];
      for (let y = START_YEAR; y < endYear; y++) {
        options.push({ text: `${y}/${y + 1}`, value: y });
      }
      return options;
    });

    const filteredLicenses = computed(() => {
      if (!selectedYear.value) return allLicenses.value;
      return allLicenses.value.filter(l => l.pegase_license_year === selectedYear.value || l.pegase_license_year === selectedYear.value + 1);
    });

    const prepareLicenseBody = license => ({
      key: `${license.license.customerUuid}_${license.license.pegase_license_year}`,
      firstname: license.customer.firstname,
      lastname: license.customer.lastname,
      email: license.customer.email,
      licenseCode: license.license.pegase_license_code || '--',
      fullname: `${license.customer.firstname} ${license.customer.lastname}`,
      orderGenericUuid: license.license.orderGenericUuid,
      customerUuid: license.license.customerUuid,
      horseType: license?.license?.horseType,
      lastLicenseYear: license.license.lastLicenseYear || '--',
      lastLicenseNumber: license.license.lastLicenseNumber || '--',
      pegase_license_year: license.license.pegase_license_year,
      nextYearLicense: license.license.nextYearLicense,
      customer: {
        firstname: license.customer.firstname,
        lastname: license.customer.lastname,
        email: license.customer.email,
        uuid: license.customer.uuid,
        gender: license.customer.gender,
        workspace: license.customer.workspace,
        birth_date: license.customer.birth_date,
        mobilephone: license.customer.mobilephone,
        telephone: license.customer.telephone,
        country: license.customer.country,
        address1: license.customer.address1,
        address2: license.customer.address2,
        town: license.customer.town,
        street_name: license.customer.street_name,
        street_number: license.customer.street_number,
        nationality: license.customer.nationality,
        zip_code: license.customer.zip_code
      }
    });

    const getLicenses = async () => {
      loading.value = true;
      allLicenses.value = [];
      const response = await getCustomersLicensesToActivate();
      if (response.success) {
        allLicenses.value = response.data.map(l => prepareLicenseBody(l));
      } else {
        sendNotification('error', 'Erreur lors de la récupération des licences');
      }
      selectedYear.value = null;
      loading.value = false;
    };

    onBeforeMount(() => {
      getLicenses();
    });

    const manageRowSelection = item => {
      if (item.horseType && !selectedItems.value.some(i => i.key === item.key)) {
        selectedItems.value.push(item);
      }
    };

    const openRefreshModal = () => { showRefreshModal.value = true; };
    const closeRefreshModal = () => { showRefreshModal.value = false; };
    const openDeleteModal = license => { selectedLicense.value = license; showDeleteModal.value = true; };
    const closeDeleteModal = () => { showDeleteModal.value = false; };

    const isButtonDisable = computed(() => !selectedItems.value.length);

    const filterDataTableSession = (value, searchValue, item) =>
      item.email.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.fullname.toLowerCase().includes(searchValue.toLowerCase());

    const confirmLabel = computed(() => {
      if (selectedItems.value.every(item => !item.lastLicenseNumber || item.lastLicenseNumber === '--')) {
        return 'Activer licence';
      }
      if (selectedItems.value.every(item => item.lastLicenseNumber)) {
        return 'Renouveler licence';
      }
      return 'Activer/Renouveler licence';
    });

    const approveLicense = async () => {
      if (!store.getters.canUserPerformAction) return;
      loading.value = true;

      if (selectedItems.value.some(item => !item.horseType)) {
        sendNotification('error', 'Veuillez vérifier le type pour chaque cavalier');
        loading.value = false;
        return;
      }

      const dataToSend = selectedItems.value.map(item => ({
        customerUuid: item.customerUuid,
        orderGenericUuid: item.orderGenericUuid,
        customer: item.customer,
        pegase_license_year: item.pegase_license_year,
        pegase_license_code: item.pegase_license_code || item.licenseCode || null,
        workspace: store.state.workspace.code.toUpperCase(),
        horseType: item.horseType,
        nextYearLicense: item.nextYearLicense
      }));

      const response = await activateCustomersLicenses(dataToSend);

      if (response.data && response.data.length) {
        response.data.forEach(item => {
          const index = allLicenses.value.findIndex(l => l.key === `${item.license.customerUuid}_${item.license.pegase_license_year}`);
          if (index !== -1) allLicenses.value.splice(index, 1);
        });
      }

      sendNotification(response.success ? 'success' : 'error', response.message);
      loading.value = false;
    };

    const licensesToUpdate = computed(() =>
      (selectedItems.value || [])
        .filter(l => l.lastLicenseNumber && l.lastLicenseNumber !== '--' && l.lastLicenseYear && l.lastLicenseYear !== '--' && l.horseType)
        .map(license => ({ ...license, fullname: `${license.firstname} ${license.lastname}`, licenseYear: license.pegase_license_year.toString() }))
    );

    const updateAllLicenses = async () => {
      if (!licensesToUpdate.value.length) {
        closeRefreshModal();
        return;
      }
      loading.value = true;

      const updatedLicenses = await refreshLicense(licensesToUpdate.value);
      const counter = { successCounter: 0, errorCounter: 0 };

      (updatedLicenses || []).forEach(item => {
        if (item.success) {
          counter.successCounter += 1;
        } else {
          counter.errorCounter += 1;
        }

        const index = allLicenses.value.findIndex(l => l.customerUuid === item.data?.customer_uuid);
        if (index !== -1) {
          allLicenses.value[index].licenseCode = item.data?.licenseCode;
          allLicenses.value[index].pegase_license_year = item.data?.pegase_license_year;
        }
      });

      sendNotification('success', `${counter.successCounter} licences mises à jour avec succès. ${counter.errorCounter} licences ont eu une erreur lors de la mise à jour.`);
      closeRefreshModal();
      loading.value = false;
    };

    const canUserPerformAction = computed(() => store.getters.canUserPerformAction);

    const deleteLicense = async () => {
      loading.value = true;
      const response = await deleteLicenseDynamo(selectedLicense.value.customerUuid, selectedLicense.value.pegase_license_year.toString());
      loading.value = false;
      closeDeleteModal();
      sendNotification(response.success ? 'success' : 'error', response.message);

      if (response.success) {
        const index = allLicenses.value.findIndex(l => l.key === selectedLicense.value.key);
        allLicenses.value.splice(index, 1);
      }
    };

    return {
      allLicenses, filteredLicenses, selectedItems, selectedYear, yearOptions,
      search, headers, refreshHeaders, filterDataTableSession,
      approveLicense, isButtonDisable, loading, canUserPerformAction,
      horseTypes, confirmLabel, manageRowSelection,
      deleteLicense, showDeleteModal, openDeleteModal, closeDeleteModal, selectedLicense,
      tableHeight, updateAllLicenses, licensesToUpdate,
      showRefreshModal, openRefreshModal, closeRefreshModal
    };
  }
};
</script>
